import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  TablePagination,
  IconButton,
  Button,
  Chip,
  Tooltip,
  Alert,
  FormControl,
  Select,
  MenuItem,
  Pagination,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Stack,
  Divider,
  Avatar,
  Badge,
  alpha,
  useTheme,
  useMediaQuery,
  Fade,
  Snackbar,
  InputLabel,
} from "@mui/material";
import {
  Search as SearchIcon,
  MedicalServices as MedicalServicesIcon,
  Chat as ChatIcon,
  Refresh as RefreshIcon,
  InfoOutlined as InfoOutlinedIcon,
  CheckCircle as CheckCircleIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CalendarToday as CalendarTodayIcon,
  AccessTime as AccessTimeIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { fetchPatients, getPatientDetails, clearSelectedPatient, fetchPatientStats } from "../../features/patients/patientSlice";
import { styled } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import PatientDetailsModal from "../../components/DoctorDashboard/PatientDetailsModal";
import debounce from "lodash.debounce";
import { useNavigate } from "react-router-dom";

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: 'blur(8px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  backgroundColor: status === 'active' ? alpha(theme.palette.success.main, 0.1) :
                  status === 'warning' ? alpha(theme.palette.warning.main, 0.1) :
                  alpha(theme.palette.error.main, 0.1),
  color: status === 'active' ? theme.palette.success.main :
         status === 'warning' ? theme.palette.warning.main :
         theme.palette.error.main,
  fontWeight: 500,
  '& .MuiChip-icon': {
    color: 'inherit',
  },
}));

const PatientManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const patientsState = useSelector((state) => state.patients);
  const patients = patientsState?.data || [];
  const total = patientsState?.total || 0;
  const loading = patientsState?.loading || false;
  const error = patientsState?.error || null;
  const selectedPatient = patientsState?.selectedPatient || null;
  const stats = patientsState?.stats || {};
  const statsLoading = patientsState?.statsLoading || false;
  
  // Debug logging
  useEffect(() => {
    console.log('👥 Patient List State:', {
      patients: patients.length,
      total,
      loading,
      error,
      rawState: patientsState
    });
  }, [patients.length, total, loading, error]);
  
  // State Management
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState(null); // Store the patient ID for the modal
  const [intakeFormOpen, setIntakeFormOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedPatientForAction, setSelectedPatientForAction] = useState(null);

  // Calculate total pages
  const totalPages = Math.ceil(total / rowsPerPage);

  // Debounced search handler
  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchTerm(value);
      setPage(1);
    }, 500),
    []
  );

  // Track initial mount
  const isInitialMount = React.useRef(true);

  // Fetch patients and stats
  useEffect(() => {
    if (isInitialMount.current) {
      // Initial load: fetch both patients and stats
      dispatch(fetchPatients({
        search: searchTerm,
        page,
        limit: rowsPerPage,
        status: statusFilter,
        sortBy,
        sortOrder,
      }));
      dispatch(fetchPatientStats());
      isInitialMount.current = false;
    } else {
      // Subsequent loads: only fetch patients (stats are independent)
      dispatch(fetchPatients({
        search: searchTerm,
        page,
        limit: rowsPerPage,
        status: statusFilter,
        sortBy,
        sortOrder,
      }));
    }
  }, [dispatch, page, rowsPerPage, searchTerm, statusFilter, sortBy, sortOrder]);

  // Refresh stats periodically (every 5 minutes)
  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(fetchPatientStats());
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [dispatch]);

  // Handlers
  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleSearch = (event) => {
    debouncedSearch(event.target.value);
  };

  const handleStatusFilter = (event) => {
    setStatusFilter(event.target.value);
    setPage(1);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleRefresh = useCallback(async () => {
    await Promise.all([
      dispatch(fetchPatients({
        search: searchTerm,
        page,
        limit: rowsPerPage,
        status: statusFilter,
        sortBy,
        sortOrder,
      })),
      dispatch(fetchPatientStats())
    ]);
  }, [dispatch, searchTerm, page, rowsPerPage, statusFilter, sortBy, sortOrder]);

  const handleViewDetails = useCallback(async (patientId, event) => {
    // Prevent row click from firing when clicking the icon button
    if (event) {
      event.stopPropagation();
    }
    
    if (!patientId) {
      console.warn('⚠️ No patient ID provided to handleViewDetails');
      return;
    }

    console.log('👤 Opening patient details for ID:', patientId);
    
    // Set the patient ID first so modal can use it immediately
    setSelectedPatientId(patientId);
    setViewDialogOpen(true);
    
    // Then fetch the patient details
    try {
      await dispatch(getPatientDetails(patientId)).unwrap();
      console.log('✅ Patient details loaded successfully');
    } catch (error) {
      console.error('❌ Error loading patient details:', error);
      setSnackbar({
        open: true,
        message: typeof error === 'string' ? error : error?.detail || 'Failed to load patient details',
        severity: 'error'
      });
    }
  }, [dispatch]);

  const handleCloseDetails = useCallback(() => {
    setViewDialogOpen(false);
    setSelectedPatientId(null);
    dispatch(clearSelectedPatient());
  }, [dispatch]);

  const handlePatientAction = (patient, action) => {
    setSelectedPatientForAction(patient);
    switch (action) {
      case 'edit':
        // Handle edit
        break;
      case 'delete':
        // Handle delete
        break;
      case 'chat':
        // Handle chat
        break;
      default:
        break;
    }
  };

  // Utility Functions
  const getTimeSinceLastConsultation = (lastDate) => {
    if (!lastDate) return "No history";

    try {
      const lastVisit = typeof lastDate === 'string' ? new Date(lastDate) : lastDate;
      if (isNaN(lastVisit.getTime())) return "Invalid date";
      
      const now = new Date();
      const diffInDays = Math.floor((now - lastVisit) / (1000 * 60 * 60 * 24));

      if (diffInDays < 0) return "Upcoming";
      if (diffInDays === 0) return "Today";
      if (diffInDays === 1) return "Yesterday";
      if (diffInDays < 7) return `${diffInDays} days ago`;
      if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
      if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
      return `${Math.floor(diffInDays / 365)} years ago`;
    } catch (e) {
      return "N/A";
    }
  };

  const hasUpcomingAppointment = (appointments) => {
    if (!appointments || !Array.isArray(appointments)) return false;
    const now = new Date();
    return appointments.some(
      (appt) => new Date(appt.scheduled_date) > now
    );
  };

  const getNextAppointment = (appointments) => {
    if (!appointments || !Array.isArray(appointments)) return null;
    const now = new Date();
    const upcomingAppointments = appointments
      .filter(appt => new Date(appt.scheduled_date) > now)
      .sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date));
    return upcomingAppointments[0] || null;
  };

  const getPatientName = (patient) => {
    if (!patient) return "N/A";
    return patient.name || patient.patient?.name || "N/A";
  };

  const getPatientContact = (patient) => {
    if (!patient) return "N/A";
    return patient.phone_number || patient.patient?.phone_number || "N/A";
  };

  const getVerificationStatus = (patient) => {
    if (!patient) return null;
    return {
      email: patient.email_verified,
      phone: patient.phone_verified
    };
  };

  return (
    <Box sx={{ padding: 3 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Patient Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your patients and their medical records
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIntakeFormOpen(true)}
            sx={{
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            }}
          >
            New Patient
          </Button>
        </Stack>
      </Box>

      {/* Stats Cards - Using Backend Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <StyledCard sx={{ background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)` }}>
              <CardContent>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      Total Patients
                    </Typography>
                    {statsLoading ? <CircularProgress size={20} /> : <PersonIcon sx={{ color: 'primary.main', fontSize: 28 }} />}
                  </Box>
                  <Typography variant="h3" fontWeight={700} color="primary.main">
                    {stats.total?.toLocaleString('en-IN') || total.toLocaleString('en-IN') || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    All patients under your care
                  </Typography>
                </Stack>
              </CardContent>
            </StyledCard>
          </motion.div>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <StyledCard sx={{ background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)` }}>
              <CardContent>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      Active Patients
                    </Typography>
                    {statsLoading ? <CircularProgress size={20} /> : <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />}
                  </Box>
                  <Typography variant="h3" fontWeight={700} color="success.main">
                    {stats.active?.toLocaleString('en-IN') || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Active in last 90 days
                  </Typography>
                </Stack>
              </CardContent>
            </StyledCard>
          </motion.div>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <StyledCard sx={{ background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)` }}>
              <CardContent>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      Upcoming Appointments
                    </Typography>
                    {statsLoading ? <CircularProgress size={20} /> : <CalendarTodayIcon sx={{ color: 'info.main', fontSize: 28 }} />}
                  </Box>
                  <Typography variant="h3" fontWeight={700} color="info.main">
                    {stats.upcoming_appointments?.toLocaleString('en-IN') || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Patients with scheduled visits
                  </Typography>
                </Stack>
              </CardContent>
            </StyledCard>
          </motion.div>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <StyledCard sx={{ background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.05)} 100%)` }}>
              <CardContent>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      New This Month
                    </Typography>
                    {statsLoading ? <CircularProgress size={20} /> : <TrendingUpIcon sx={{ color: 'warning.main', fontSize: 28 }} />}
                  </Box>
                  <Typography variant="h3" fontWeight={700} color="warning.main">
                    {stats.new_this_month?.toLocaleString('en-IN') || 0}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {stats.new_patients_trend !== undefined && stats.new_patients_trend !== 0 && (
                      <>
                        {stats.new_patients_trend > 0 ? (
                          <TrendingUpIcon sx={{ color: 'success.main', fontSize: 16 }} />
                        ) : (
                          <TrendingDownIcon sx={{ color: 'error.main', fontSize: 16 }} />
                        )}
                        <Typography 
                          variant="caption" 
                          color={stats.new_patients_trend > 0 ? 'success.main' : 'error.main'}
                          fontWeight={600}
                        >
                          {stats.new_patients_trend > 0 ? '+' : ''}{stats.new_patients_trend} vs last month
                        </Typography>
                      </>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </StyledCard>
          </motion.div>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search patients by name, contact, or ID..."
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status Filter</InputLabel>
              <Select value={statusFilter} onChange={handleStatusFilter}>
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Loading & Error Handling */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="400px">
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      ) : patients.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: 'center', p: 3 }}>
          No patients found
        </Typography>
      ) : (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            {/* Patients Table */}
            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Patient
                        <IconButton size="small" onClick={() => handleSort('name')}>
                          <SortIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Verification</TableCell>
                    <TableCell>Last Visit</TableCell>
                    <TableCell>Next Appointment</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : error ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Alert severity="error">
                          {typeof error === 'string' ? error : error?.detail || 'Failed to load patients'}
                        </Alert>
                        <Button 
                          variant="outlined" 
                          onClick={handleRefresh}
                          sx={{ mt: 2 }}
                        >
                          Retry
                        </Button>
                      </TableCell>
                    </TableRow>
                  ) : patients.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          No patients found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    patients.map((patient) => {
                      const verificationStatus = getVerificationStatus(patient);
                      // Get appointments from patient object or fetch separately
                      const patientAppointments = patient.appointments || patient.patient_appointments || [];
                      const nextAppointment = getNextAppointment(patientAppointments);
                    
                    return (
                      <TableRow
                        key={patient?.id || Math.random()}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={(e) => {
                          // Only open details if clicking directly on the row, not on action buttons
                          if (e.target.closest('button, a, [role="button"]')) {
                            return; // Let the button handle its own click
                          }
                          if (patient?.id) {
                            handleViewDetails(patient.id, null);
                          }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                              src={patient.avatar}
                              alt={getPatientName(patient)}
                              sx={{ width: 40, height: 40 }}
                            >
                              {getPatientName(patient).charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600}>
                                {getPatientName(patient)}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                ID: {patient.id}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PhoneIcon fontSize="small" color="action" />
                              <Typography variant="body2">
                                {getPatientContact(patient)}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <EmailIcon fontSize="small" color="action" />
                              <Typography variant="body2">
                                {patient.email || 'N/A'}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            {verificationStatus?.email ? (
                              <Tooltip title="Email Verified">
                                <CheckCircleIcon color="success" fontSize="small" />
                              </Tooltip>
                            ) : (
                              <Tooltip title="Email Not Verified">
                                <WarningIcon color="warning" fontSize="small" />
                              </Tooltip>
                            )}
                            {verificationStatus?.phone ? (
                              <Tooltip title="Phone Verified">
                                <CheckCircleIcon color="success" fontSize="small" />
                              </Tooltip>
                            ) : (
                              <Tooltip title="Phone Not Verified">
                                <WarningIcon color="warning" fontSize="small" />
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            {(() => {
                              // Try to get last appointment from various possible fields
                              const lastApptDate = patient?.last_appointment || 
                                                   patient?.appointment_stats?.last_appointment_date ||
                                                   (patientAppointments && patientAppointments.length > 0 
                                                     ? patientAppointments.sort((a, b) => {
                                                         const dateA = new Date(a.scheduled_date || 0);
                                                         const dateB = new Date(b.scheduled_date || 0);
                                                         return dateB - dateA;
                                                       })[0]?.scheduled_date
                                                     : null);
                              
                              const lastApptStatus = patient?.last_appointment_status || null;
                              
                              return (
                                <>
                                  <Typography variant="body2" fontWeight={500}>
                                    {lastApptDate 
                                      ? (typeof lastApptDate === 'string' 
                                          ? new Date(lastApptDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                                          : lastApptDate.toLocaleDateString?.('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) || 'N/A')
                                      : "No visits"}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary">
                                      {getTimeSinceLastConsultation(lastApptDate)}
                                    </Typography>
                                    {lastApptStatus && (
                                      <Chip 
                                        label={lastApptStatus}
                                        size="small"
                                        sx={{ 
                                          height: 18, 
                                          fontSize: '0.65rem',
                                          backgroundColor: lastApptStatus === 'Completed' 
                                            ? alpha(theme.palette.success.main, 0.1)
                                            : lastApptStatus === 'Pending'
                                            ? alpha(theme.palette.warning.main, 0.1)
                                            : alpha(theme.palette.grey[500], 0.1),
                                          color: lastApptStatus === 'Completed'
                                            ? theme.palette.success.main
                                            : lastApptStatus === 'Pending'
                                            ? theme.palette.warning.main
                                            : theme.palette.grey[600]
                                        }}
                                      />
                                    )}
                                  </Box>
                                </>
                              );
                            })()}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {nextAppointment ? (
                            <Stack spacing={0.5}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarTodayIcon fontSize="small" color="action" />
                                <Typography variant="body2" color="primary">
                                  {nextAppointment.scheduled_date 
                                    ? (typeof nextAppointment.scheduled_date === 'string'
                                        ? new Date(nextAppointment.scheduled_date).toLocaleDateString()
                                        : nextAppointment.scheduled_date.toLocaleDateString?.() || 'N/A')
                                    : 'N/A'}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AccessTimeIcon fontSize="small" color="action" />
                                <Typography variant="caption" color="text.secondary">
                                  {nextAppointment.type || nextAppointment.consultation_type || "Consultation"}
                                </Typography>
                              </Box>
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No upcoming
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography variant="body2" fontWeight={500}>
                              Total: {patient.appointment_stats?.total || patientAppointments.length || 0}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {(patient.appointment_stats?.completed || 0) > 0 && (
                                <Chip 
                                  label={`${patient.appointment_stats.completed} completed`}
                                  size="small" 
                                  color="success"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              )}
                              {(patient.appointment_stats?.upcoming || 0) > 0 && (
                                <Chip 
                                  label={`${patient.appointment_stats.upcoming} upcoming`}
                                  size="small" 
                                  color="warning"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewDetails(patient.id, e);
                                }}
                                sx={{
                                  color: theme.palette.primary.main,
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                  },
                                }}
                              >
                                <InfoOutlinedIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Start Chat">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePatientAction(patient, 'chat');
                                }}
                                sx={{
                                  color: theme.palette.secondary.main,
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                                  },
                                }}
                              >
                                <ChatIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Medical Records">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePatientAction(patient, 'medical');
                                }}
                                sx={{
                                  color: theme.palette.success.main,
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                                  },
                                }}
                              >
                                <MedicalServicesIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {((page - 1) * rowsPerPage) + 1} to {Math.min(page * rowsPerPage, total)} of {total} patients
                </Typography>
                <FormControl size="small">
                  <Select
                    value={rowsPerPage}
                    onChange={handleChangeRowsPerPage}
                    sx={{ minWidth: 120 }}
                  >
                    <MenuItem value={5}>5 per page</MenuItem>
                    <MenuItem value={10}>10 per page</MenuItem>
                    <MenuItem value={25}>25 per page</MenuItem>
                    <MenuItem value={50}>50 per page</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handleChangePage}
                color="primary"
                size={isMobile ? "small" : "medium"}
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    },
                  },
                }}
              />
            </Box>
          </motion.div>
        </AnimatePresence>
      )}

      {/* Patient Details Modal */}
      <PatientDetailsModal
        open={viewDialogOpen}
        onClose={handleCloseDetails}
        patientId={selectedPatientId || selectedPatient?.id}
      />

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PatientManagement;
